import { ComponentChildren } from 'preact'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type { ButtonStandardProps } from '@bonniernews/dn-design-system-web/components/button/button-types.ts'
import Button from '@bonniernews/dn-design-system-web/components/button/button.tsx'

export interface ModalProps {
  /** Title in modal */
  title?: string
  /** Appearance of the modal */
  modalType?: 'standard' | 'sheet'
  /** Object with text, href (optional), classNames (optional) and attributes (optional) */
  primaryButton?: ButtonStandardProps
  /** Object with text, href (optional), classNames (optional) and attributes (optional). Set isCloseButton to true if you wanna close modal with this button */
  secondaryButton?: ButtonStandardProps & { isCloseButton?: boolean }
  /** Ex. "my-special-class" */
  classNames?: string
  /** Ex. { data-prop: value } */
  attributes?: object
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
  /** Content to show in the modal as preact components */
  children?: ComponentChildren
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/Modal](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/modal)
 * - Storybook: [Modal](https://designsystem.dn.se/?path=/docs/basic-modal--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/modal/modal.scss'`
 */
export const Modal = ({
  title,
  modalType = 'standard',
  primaryButton,
  secondaryButton,
  classNames,
  attributes,
  forcePx,
  children,
}: ModalProps) => {
  const componentClassName = 'ds-modal'

  const classes = formatClassString([
    componentClassName,
    modalType === 'sheet' && `${componentClassName}--sheet`,
    forcePx && 'ds-force-px',
    'ds-modal--hidden', // Add default hidden class to prevent flashing modal in older browsers where dialog isn't supported
    classNames,
  ])

  const hasButtons = primaryButton || secondaryButton

  return (
    <div className={classes}>
      <dialog className={`${componentClassName}__inner`} {...attributes}>
        <div className={`${componentClassName}__content`}>
          {modalType === 'standard' && (
            <Button
              {...{
                variant: 'transparent',
                size: 'sm',
                iconName: 'close',
                isIconButton: true,
                attributes: {
                  'aria-label': 'stäng',
                },
                classNames: 'ds-modal__close ds-modal__close--icon',
              }}
            />
          )}

          {title && <h2>{title}</h2>}

          {children}

          {hasButtons && (
            <div className='ds-modal__buttons'>
              {secondaryButton && (
                <Button
                  {...{
                    text: secondaryButton.text,
                    size: 'lg',
                    variant: 'secondaryOutline',
                    href: secondaryButton.href,
                    classNames: [
                      'ds-modal__secondary-button',
                      secondaryButton.isCloseButton && 'ds-modal__close',
                      secondaryButton.classNames,
                    ].join(' '),
                    attributes: secondaryButton.attributes,
                    forcePx,
                    mobileFullWidth: true,
                  }}
                />
              )}

              {primaryButton && (
                <Button
                  {...{
                    text: primaryButton.text,
                    size: 'lg',
                    variant: 'primaryBlack',
                    href: primaryButton.href,
                    classNames: ['ds-modal__primary-button', primaryButton.classNames].join(' '),
                    attributes: primaryButton.attributes,
                    forcePx,
                    mobileFullWidth: true,
                  }}
                />
              )}
            </div>
          )}
        </div>
      </dialog>
    </div>
  )
}
